import { memo, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { Loader2, Play, Scissors } from 'lucide-react'

import { ProtoDropdown, ProtoInput, ProtoNumberInput } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import type { AIModel } from '@/api/types'
import { handleColor } from '@/features/studio/canvas/handles'
import {
  advancedAudioParamFields,
  buildClonedVoiceOptions,
  buildDesignedVoiceOptions,
  isDashScopeClonedVoiceId,
  isDashScopeDesignedVoiceId,
  isElevenLabsMusicModel,
  isElevenLabsTTSModel,
  isMurekaMusicModel,
  isIndexTTSModel,
  isMiniMaxSpeechModel,
  isQwenVCRealtimeModel,
  isQwenVDRealtimeModel,
  isSeedAudioModel,
  normalizeAudioSubmitPayload,
  primaryAudioParamFields,
  usesDedicatedAudioPanel,
} from '@/features/studio/canvas/audio-params'
import { filterCompatibleAudioModels } from '@/features/studio/canvas/audio-compat'
import { MiniMaxSpeechPanel } from '@/features/studio/canvas/nodes/MiniMaxSpeechPanel'
import { MurekaMusicPanel } from '@/features/studio/canvas/nodes/MurekaMusicPanel'
import { SeedAudioPanel } from '@/features/studio/canvas/nodes/SeedAudioPanel'
import {
  applyAudioModelSelection,
  AUDIO_TAB_HINT_KEYS,
  VOICE_SOURCE_OPTIONS,
  filterAudioModelsByTab,
  getActiveAudioTab,
  patchAudioTab,
  productTabFromUi,
  switchAudioTab,
  topTabFromProductTab,
  voiceSourceFromProductTab,
  type VoiceSource,
} from '@/features/studio/canvas/audio-tabs'
import type { AudioProductTab } from '@/features/studio/canvas/types'
import { buildCanvasModelOptions } from '@/features/studio/canvas/model-options'
import type { ModelParamField } from '@/features/studio/canvas/model-params'
import { NodeChip, NodeChipStrip, NodeHero } from '@/features/studio/canvas/nodes/NodeChrome'
import type { HandleDataType, StudioNodeData } from '@/features/studio/canvas/types'
import { AudioWavePlayer } from '@/features/studio/components/AudioWavePlayer'
import { aiApi } from '@/api/ai'
import { formatResolvedApiError, resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'

type UpstreamPreview = {
  nodeId: string
  label: string
  url?: string
  dataType?: HandleDataType
}

type CanvasT = (key: string, opts?: Record<string, unknown>) => string

function hasUpstreamAudio(upstream: UpstreamPreview[]): boolean {
  return upstream.some((p) => p.dataType === 'audio' && !!p.url)
}

export const AudioGenBody = memo(function AudioGenBody({
  id,
  data,
  showChrome = false,
  audioModels,
  previewUrl,
  upstream,
  onPatch,
  onRun,
  onTrimAudio,
  runDisabled,
  runRunning,
  t,
}: {
  id: string
  data: StudioNodeData
  showChrome?: boolean
  audioModels: AIModel[]
  previewUrl?: string
  upstream: UpstreamPreview[]
  nodeTitle: string
  onPatch: (
    id: string,
    patch: Partial<StudioNodeData> | ((data: StudioNodeData) => Partial<StudioNodeData>),
  ) => void
  onRun: () => void
  onTrimAudio?: (nodeId: string, startSec: number, endSec: number) => void | Promise<void>
  runDisabled?: boolean
  runRunning?: boolean
  t: CanvasT
}) {
  const activeTab = getActiveAudioTab(data)
  const topTab = topTabFromProductTab(activeTab)
  const voiceSource = voiceSourceFromProductTab(activeTab)
  const tabModels = useMemo(() => filterAudioModelsByTab(audioModels, activeTab), [audioModels, activeTab])
  const selectedModel = tabModels.find((m) => m.id === (data.gen?.modelId ?? ''))
  const [clonedVoiceOptions, setClonedVoiceOptions] = useState<{ value: string; labelKey: string }[]>([])
  const [designedVoiceOptions, setDesignedVoiceOptions] = useState<{ value: string; labelKey: string }[]>([])
  const [creatingDesignedVoice, setCreatingDesignedVoice] = useState(false)
  const [designedVoiceError, setDesignedVoiceError] = useState<string | null>(null)
  const [trimStart, setTrimStart] = useState(0)
  const [trimEnd, setTrimEnd] = useState(10)
  const [trimming, setTrimming] = useState(false)
  const [, setLastVoiceSource] = useState<VoiceSource>(() =>
    voiceSourceFromProductTab(getActiveAudioTab(data)),
  )

  useEffect(() => {
    if (topTab === 'dubbing') setLastVoiceSource(voiceSource)
  }, [topTab, voiceSource])

  useEffect(() => {
    const modelId = data.gen?.modelId
    if (!modelId || !isQwenVCRealtimeModel(modelId) || activeTab !== 'voice_clone') {
      setClonedVoiceOptions([])
      return
    }
    let cancelled = false
    aiApi
      .listDashScopeClonedVoices({ model_id: modelId, page_size: 50 })
      .then((res) => {
        if (!cancelled) setClonedVoiceOptions(buildClonedVoiceOptions(res.items ?? []))
      })
      .catch(() => {
        if (!cancelled) setClonedVoiceOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [activeTab, data.gen?.modelId])

  useEffect(() => {
    const modelId = data.gen?.modelId
    if (!modelId || !isQwenVDRealtimeModel(modelId) || activeTab !== 'voice_design') {
      setDesignedVoiceOptions([])
      return
    }
    let cancelled = false
    aiApi
      .listDashScopeDesignedVoices({ model_id: modelId, page_size: 50 })
      .then((res) => {
        if (!cancelled) setDesignedVoiceOptions(buildDesignedVoiceOptions(res.items ?? []))
      })
      .catch(() => {
        if (!cancelled) setDesignedVoiceOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [activeTab, data.gen?.modelId])

  const hasModelForActive = !!String(data.gen?.modelId ?? '').trim() && tabModels.length > 0
  const primaryFields =
    topTab === 'music' || hasModelForActive
      ? primaryAudioParamFields(
        data.gen?.modelId,
        selectedModel,
        activeTab,
        clonedVoiceOptions,
        designedVoiceOptions,
        data.gen?.params,
      )
      : []
  const advancedFields =
    topTab === 'music' || hasModelForActive
      ? advancedAudioParamFields(
        data.gen?.modelId,
        selectedModel,
        activeTab,
        clonedVoiceOptions,
        designedVoiceOptions,
        data.gen?.params,
      )
      : []
  const modelOptions = useMemo(
    () => buildCanvasModelOptions(filterCompatibleAudioModels(audioModels), t),
    [audioModels, t],
  )

  const setGen = (patch: { modelId?: string; params?: Record<string, string | number> }) => {
    const tabPatch: { modelId?: string; params?: Record<string, string | number> } = {}
    if (patch.modelId !== undefined) tabPatch.modelId = patch.modelId
    if (patch.params) tabPatch.params = patch.params
    onPatch(id, patchAudioTab(data, activeTab, tabPatch, audioModels))
  }

  const onProductTabChange = (tab: AudioProductTab) => {
    if (tab === activeTab) return
    const sw = switchAudioTab(data, tab, audioModels)
    const merged = { ...data, ...sw } as typeof data
    const modelId = merged.gen?.modelId ?? ''
    const params = { ...(merged.gen?.params ?? {}) }
    const vid = String(params.voice_id ?? '').trim()
    if (tab === 'voice_clone' && isQwenVCRealtimeModel(modelId) && isDashScopeDesignedVoiceId(vid)) {
      delete params.voice_id
      delete params.voice_prompt
      onPatch(id, { ...sw, ...patchAudioTab(merged, tab, { params }, audioModels) })
      return
    }
    if (tab === 'voice_design' && isQwenVDRealtimeModel(modelId) && vid && !isDashScopeDesignedVoiceId(vid)) {
      delete params.voice_id
      onPatch(id, { ...sw, ...patchAudioTab(merged, tab, { params }, audioModels) })
      return
    }
    onPatch(id, sw)
  }

  const onVoiceSourceChange = (source: VoiceSource) => {
    setLastVoiceSource(source)
    onProductTabChange(productTabFromUi('dubbing', source))
  }

  // 克隆 Tab 误留设计音色时立即清空，避免提交到 VC 模型。
  useEffect(() => {
    const modelId = data.gen?.modelId
    if (!modelId || activeTab !== 'voice_clone' || !isQwenVCRealtimeModel(modelId)) return
    const vid = String(data.gen?.params?.voice_id ?? '').trim()
    if (!isDashScopeDesignedVoiceId(vid)) return
    const params = { ...(data.gen?.params ?? {}) }
    delete params.voice_id
    delete params.voice_prompt
    setGen({ params })
  }, [activeTab, data.gen?.modelId, data.gen?.params?.voice_id])

  // 设计/克隆音色拉取后，若 params 仍为旁白遗留或跨 Tab 音色，自动选中第一项。
  useEffect(() => {
    const modelId = data.gen?.modelId
    if (!modelId) return
    const cur = String(data.gen?.params?.voice_id ?? '').trim()
    if (activeTab === 'voice_design' && isQwenVDRealtimeModel(modelId) && designedVoiceOptions.length) {
      if (!isDashScopeDesignedVoiceId(cur)) {
        setGen({ params: { ...data.gen?.params, voice_id: designedVoiceOptions[0].value } })
      }
    }
    if (activeTab === 'voice_clone' && isQwenVCRealtimeModel(modelId) && clonedVoiceOptions.length) {
      if (!isDashScopeClonedVoiceId(cur)) {
        setGen({ params: { ...data.gen?.params, voice_id: clonedVoiceOptions[0].value } })
      }
    }
  }, [activeTab, clonedVoiceOptions, designedVoiceOptions, data.gen?.modelId, data.gen?.params?.voice_id])

  const buildModelParams = (modelId: string, current?: Record<string, string | number>) => {
    const params = { ...(current ?? {}) }
    delete params.voice_id
    delete params.voice
    delete params.voice_prompt
    delete params.instructions
    delete params.lyrics
    delete params.style
    if (isElevenLabsTTSModel(modelId)) {
      params.voice_id = String(params.voice_id ?? '').trim() || 'kAIqZ7fZv234ClKXwzDx'
      params.stability = String(params.stability ?? 'natural')
      delete params.duration
    }
    if (isElevenLabsMusicModel(modelId)) {
      const sec = Number(params.duration ?? 30)
      params.duration = [30, 60, 120, 180, 240].includes(sec) ? sec : 30
      delete params.stability
    }
    if (isSeedAudioModel(modelId)) {
      const next = normalizeAudioSubmitPayload(modelId, {
        ...params,
        language: params.language ?? 'zh',
        sample_rate: params.sample_rate ?? 24000,
        format: params.format ?? 'wav',
        speed: params.speed ?? 1,
        pitch: params.pitch ?? 0,
        volume: params.volume ?? 1,
      })
      return Object.fromEntries(
        Object.entries(next).filter(([, v]) => v !== undefined && typeof v !== 'object'),
      ) as Record<string, string | number>
    }
    if (isMiniMaxSpeechModel(modelId)) {
      const next = normalizeAudioSubmitPayload(modelId, {
        ...params,
        voice_id: params.voice_id ?? 'male-qn-qingse',
        speed: params.speed ?? 1,
        pitch: params.pitch ?? 0,
        volume: params.volume ?? 1,
        modify_pitch: params.modify_pitch ?? 0,
        intensity: params.intensity ?? 0,
        timbre: params.timbre ?? 0,
        sound_effects: params.sound_effects ?? 'none',
      })
      return Object.fromEntries(
        Object.entries(next).filter(([, v]) => v !== undefined && typeof v !== 'object'),
      ) as Record<string, string | number>
    }
    if (isMurekaMusicModel(modelId)) {
      const next = normalizeAudioSubmitPayload(modelId, {
        ...params,
        music_mode: params.music_mode ?? 'describe',
        instrumental: params.instrumental ?? 'false',
      })
      return Object.fromEntries(
        Object.entries(next).filter(([, v]) => v !== undefined && typeof v !== 'object'),
      ) as Record<string, string | number>
    }
    return params
  }

  const onModelChange = (modelId: string) => {
    onPatch(id, (current) =>
      applyAudioModelSelection(current, modelId, buildModelParams(modelId, current.gen?.params), audioModels),
    )
  }

  const renderParamField = (field: ModelParamField) => {
    const val = data.gen?.params?.[field.key] ?? field.default
    const label = t(field.labelKey, { defaultValue: field.key })
    return (
      <div key={field.key} className="cn-gen-controls__cell">
        <span className="cn-gen-controls__label">{label}</span>
        {field.type === 'select' && field.options ? (
          <ProtoDropdown
            compact
            portal
            className="cn-gen-controls__field"
            value={String(val)}
            onValueChange={(v) => setGen({ params: { ...data.gen?.params, [field.key]: v } })}
            options={field.options.map((o) => ({
              value: o.value,
              label: t(o.labelKey, { defaultValue: o.labelKey }),
            }))}
            aria-label={label}
          />
        ) : field.type === 'number' ? (
          field.step != null && field.step < 1 ? (
            <ProtoInput
              className="cn-gen-controls__field cn-gen-controls__number"
              type="number"
              inputMode="decimal"
              step={field.step}
              min={field.min}
              max={field.max}
              value={String(val)}
              onChange={(e) => {
                const n = Number(e.target.value)
                setGen({
                  params: {
                    ...data.gen?.params,
                    [field.key]: Number.isFinite(n) ? n : field.default,
                  },
                })
              }}
              aria-label={label}
            />
          ) : (
            <ProtoNumberInput
              className="cn-gen-controls__field cn-gen-controls__number"
              value={Number(val)}
              min={field.min}
              max={field.max}
              onValueChange={(n) => setGen({ params: { ...data.gen?.params, [field.key]: n } })}
              aria-label={label}
            />
          )
        ) : field.type === 'text' ? (
          <ProtoInput
            className="cn-gen-controls__field"
            value={String(val ?? '')}
            placeholder={label}
            onChange={(e) => setGen({ params: { ...data.gen?.params, [field.key]: e.target.value } })}
            aria-label={label}
          />
        ) : null}
      </div>
    )
  }

  const tabHintKey = isSeedAudioModel(data.gen?.modelId)
    ? 'canvas.audioTab.hintSeedAudio'
    : isMiniMaxSpeechModel(data.gen?.modelId)
      ? 'canvas.audioTab.hintMiniMax'
      : isElevenLabsTTSModel(data.gen?.modelId)
        ? 'canvas.audioTab.hintElevenTTS'
        : isElevenLabsMusicModel(data.gen?.modelId)
          ? 'canvas.audioTab.hintElevenMusic'
          : isMurekaMusicModel(data.gen?.modelId)
            ? 'canvas.audioTab.hintMureka'
            : AUDIO_TAB_HINT_KEYS[activeTab]
  const cloneNeedsRef = activeTab === 'voice_clone' && isIndexTTSModel(data.gen?.modelId) && !hasUpstreamAudio(upstream)
  const vcEmpty = activeTab === 'voice_clone' && isQwenVCRealtimeModel(data.gen?.modelId) && !clonedVoiceOptions.length
  const vcVoiceMismatch =
    activeTab === 'voice_clone' &&
    isQwenVCRealtimeModel(data.gen?.modelId) &&
    isDashScopeDesignedVoiceId(String(data.gen?.params?.voice_id ?? ''))
  const vdVoiceMismatch =
    activeTab === 'voice_design' &&
    isQwenVDRealtimeModel(data.gen?.modelId) &&
    !!String(data.gen?.params?.voice_id ?? '').trim() &&
    !isDashScopeDesignedVoiceId(String(data.gen?.params?.voice_id ?? ''))
  const vcNeedsClone =
    activeTab === 'voice_clone' &&
    isQwenVCRealtimeModel(data.gen?.modelId) &&
    !isDashScopeClonedVoiceId(String(data.gen?.params?.voice_id ?? '')) &&
    !clonedVoiceOptions.some((o) => o.value === String(data.gen?.params?.voice_id ?? '').trim())
  const vdEmpty =
    activeTab === 'voice_design' &&
    isQwenVDRealtimeModel(data.gen?.modelId) &&
    !isDashScopeDesignedVoiceId(String(data.gen?.params?.voice_id ?? '')) &&
    !designedVoiceOptions.length
  const voicePrompt = String(data.gen?.params?.voice_prompt ?? '').trim()
  const canCreateDesignedVoice =
    activeTab === 'voice_design' && isQwenVDRealtimeModel(data.gen?.modelId) && !!voicePrompt && !!data.gen?.modelId

  const handleCreateDesignedVoice = async () => {
    const modelId = data.gen?.modelId
    if (!modelId || !voicePrompt) return
    setCreatingDesignedVoice(true)
    setDesignedVoiceError(null)
    try {
      const res = await aiApi.createDashScopeDesignedVoice({
        model_id: modelId,
        voice_prompt: voicePrompt,
      })
      const list = await aiApi.listDashScopeDesignedVoices({ model_id: modelId, page_size: 50 })
      const options = buildDesignedVoiceOptions(list.items ?? [])
      setDesignedVoiceOptions(options)
      setGen({ params: { ...data.gen?.params, voice_id: res.voice } })
    } catch (e) {
      setDesignedVoiceError(
        formatResolvedApiError(t, resolveApiErrorMessage(e, 'canvas.audioTab.createDesignedVoiceFailed')),
      )
    } finally {
      setCreatingDesignedVoice(false)
    }
  }

  return (
    <div className="cn-gen-layout">
      <NodeHero className="cn-hero--gen cn-hero--audio" empty={!previewUrl ? t('canvas.previewEmpty') : undefined}>
        {previewUrl ? (
          <AudioWavePlayer
            variant="hero"
            src={previewUrl}
            interactiveClassName="nodrag nopan nowheel"
          />
        ) : null}
        {showChrome && upstream.length > 0 ? (
          <div className="cn-gen-upstream">
            <NodeChipStrip>
              {upstream.map((p) => (
                <NodeChip
                  key={p.nodeId}
                  label={p.label}
                  sublabel={p.dataType ?? 'ref'}
                  color={p.dataType ? handleColor(p.dataType) : undefined}
                  thumbnail={
                    p.url ? (
                      p.dataType === 'audio' ? (
                        <span>♪</span>
                      ) : (
                        <img src={resolveMediaDisplayUrl(p.url)} alt="" />
                      )
                    ) : (
                      '—'
                    )
                  }
                />
              ))}
            </NodeChipStrip>
          </div>
        ) : null}
      </NodeHero>

      {showChrome && previewUrl && onTrimAudio ? (
        <div className="cn-gen-controls nodrag nopan nowheel cn-audio-trim">
          <div className="cn-gen-controls__grid cn-gen-controls__grid--primary">
            <div className="cn-gen-controls__cell">
              <span className="cn-gen-controls__label">{t('canvas.audioTrim.start')}</span>
              <ProtoNumberInput
                className="cn-gen-controls__field cn-gen-controls__number"
                value={trimStart}
                min={0}
                max={Math.max(0, trimEnd - 0.1)}
                onValueChange={(n) => setTrimStart(n)}
                aria-label={t('canvas.audioTrim.start')}
              />
            </div>
            <div className="cn-gen-controls__cell">
              <span className="cn-gen-controls__label">{t('canvas.audioTrim.end')}</span>
              <ProtoNumberInput
                className="cn-gen-controls__field cn-gen-controls__number"
                value={trimEnd}
                min={trimStart + 0.1}
                max={3600}
                onValueChange={(n) => setTrimEnd(n)}
                aria-label={t('canvas.audioTrim.end')}
              />
            </div>
            <div className="cn-gen-controls__cell cn-gen-controls__cell--action">
              <span className="cn-gen-controls__label">&nbsp;</span>
              <Button
                type="button"
                variant="accent"
                size="sm"
                disabled={trimming || trimEnd <= trimStart}
                onClick={() => {
                  void (async () => {
                    setTrimming(true)
                    try {
                      await onTrimAudio(id, trimStart, trimEnd)
                    } finally {
                      setTrimming(false)
                    }
                  })()
                }}
              >
                {trimming ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Scissors className="h-3.5 w-3.5" />
                )}
                <span>{t('canvas.audioTrim.action')}</span>
              </Button>
            </div>
          </div>
          <p className="cn-audio-tabs__hint">{t('canvas.audioTrim.hint')}</p>
        </div>
      ) : null}

      {showChrome && isSeedAudioModel(data.gen?.modelId) ? (
        <SeedAudioPanel
          modelId={data.gen?.modelId ?? ''}
          models={filterCompatibleAudioModels(audioModels)}
          params={data.gen?.params}
          promptLength={(data.prompt?.positive ?? '').length}
          cost={audioModels.find((m) => m.id === data.gen?.modelId)?.cost ?? selectedModel?.cost ?? 1}
          runDisabled={runDisabled}
          runRunning={runRunning}
          onModelChange={onModelChange}
          onParamsChange={(patch) => setGen({ params: { ...data.gen?.params, ...patch } })}
          onRun={onRun}
          t={t}
        />
      ) : null}

      {showChrome && isMiniMaxSpeechModel(data.gen?.modelId) ? (
        <MiniMaxSpeechPanel
          modelId={data.gen?.modelId ?? ''}
          models={filterCompatibleAudioModels(audioModels)}
          params={data.gen?.params}
          promptLength={(data.prompt?.positive ?? '').length}
          cost={audioModels.find((m) => m.id === data.gen?.modelId)?.cost ?? selectedModel?.cost ?? 1}
          runDisabled={runDisabled}
          runRunning={runRunning}
          onModelChange={onModelChange}
          onParamsChange={(patch) => setGen({ params: { ...data.gen?.params, ...patch } })}
          onRun={onRun}
          t={t}
        />
      ) : null}

      {showChrome && isMurekaMusicModel(data.gen?.modelId) ? (
        <MurekaMusicPanel
          modelId={data.gen?.modelId ?? ''}
          models={filterCompatibleAudioModels(audioModels)}
          params={data.gen?.params}
          promptLength={(data.prompt?.positive ?? '').length}
          cost={audioModels.find((m) => m.id === data.gen?.modelId)?.cost ?? selectedModel?.cost ?? 1}
          runDisabled={runDisabled}
          runRunning={runRunning}
          onModelChange={onModelChange}
          onParamsChange={(patch) => setGen({ params: { ...data.gen?.params, ...patch } })}
          onRun={onRun}
          t={t}
        />
      ) : null}

      {showChrome && !usesDedicatedAudioPanel(data.gen?.modelId) ? (
        <div className="cn-gen-controls nodrag nopan nowheel">
          {topTab !== 'music' ? (
            <div className="cn-gen-controls__grid cn-gen-controls__grid--primary">
              <div className="cn-gen-controls__cell">
                <span className="cn-gen-controls__label">{t('canvas.audioTab.voiceSource')}</span>
                <ProtoDropdown
                  compact
                  portal
                  className="cn-gen-controls__field"
                  value={voiceSource}
                  onValueChange={(v) => onVoiceSourceChange(v as VoiceSource)}
                  options={VOICE_SOURCE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: t(o.labelKey),
                  }))}
                  aria-label={t('canvas.audioTab.voiceSource')}
                />
              </div>
            </div>
          ) : null}

          {tabHintKey ? <p className="cn-audio-tabs__hint">{t(tabHintKey)}</p> : null}
          {activeTab === 'music' && !isElevenLabsMusicModel(data.gen?.modelId) ? (
            <p className="cn-audio-tabs__hint">
              {String(data.gen?.params?.music_mode ?? 'describe') === 'lyrics'
                ? t('canvas.audioTab.hintMusicLyrics')
                : t('canvas.audioTab.hintMusicDescribe')}
            </p>
          ) : null}
          {cloneNeedsRef ? <p className="cn-gen-controls__warn">{t('canvas.audioTab.needRefAudio')}</p> : null}
          {vcVoiceMismatch ? (
            <p className="cn-gen-controls__warn">{t('canvas.params.voiceCloneWrongKind')}</p>
          ) : null}
          {vcEmpty ? (
            <p className="cn-gen-controls__warn">
              {hasUpstreamAudio(upstream)
                ? t('canvas.params.voiceCloneEmptyEnroll')
                : t('canvas.params.voiceCloneEmpty')}
            </p>
          ) : null}
          {vdVoiceMismatch ? (
            <p className="cn-gen-controls__warn">{t('canvas.params.voiceDesignWrongKind')}</p>
          ) : null}
          {vdEmpty ? <p className="cn-gen-controls__warn">{t('canvas.params.designedVoiceEmpty')}</p> : null}
          {designedVoiceError ? <p className="cn-gen-controls__warn">{designedVoiceError}</p> : null}
          {activeTab === 'voice_design' && isQwenVDRealtimeModel(data.gen?.modelId) ? (
            <div className="cn-gen-controls__row">
              <Button
                type="button"
                variant={canCreateDesignedVoice ? 'accent' : 'link'}
                size="sm"
                className={clsx(
                  'cn-gen-controls__link-btn h-auto',
                  canCreateDesignedVoice ? 'px-2' : 'p-0',
                )}
                disabled={!canCreateDesignedVoice || creatingDesignedVoice}
                onClick={() => void handleCreateDesignedVoice()}
              >
                {creatingDesignedVoice ? (
                  <>
                    <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
                    {t('canvas.audioTab.creatingDesignedVoice')}
                  </>
                ) : (
                  t('canvas.audioTab.createDesignedVoice')
                )}
              </Button>
            </div>
          ) : null}

          {primaryFields.length > 0 ? (
            <div className="cn-gen-controls__grid cn-gen-controls__grid--primary">
              {primaryFields.map(renderParamField)}
            </div>
          ) : null}
          {advancedFields.length > 0 ? (
            <details className="cn-gen-controls__advanced">
              <summary className="cn-gen-controls__advanced-summary">{t('canvas.params.more')}</summary>
              <div className="cn-gen-controls__grid cn-gen-controls__grid--advanced">
                {advancedFields.map(renderParamField)}
              </div>
            </details>
          ) : null}
          <div className="cn-gen-controls__footer">
            <ProtoDropdown
              compact
              portal
              className="cn-gen-controls__model"
              value={data.gen?.modelId ?? ''}
              onValueChange={onModelChange}
              options={modelOptions}
              disabled={!modelOptions.length}
              placeholder={t('canvas.section.model')}
            />
            <Button
              type="button"
              variant="default"
              size="sm"
              disabled={runDisabled || cloneNeedsRef || vcNeedsClone || vcVoiceMismatch || vdVoiceMismatch || (vdEmpty && activeTab === 'voice_design')}
              onClick={onRun}
              className="cn-run cn-run--primary cn-gen-controls__run"
            >
              {runRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              <span>{t('canvas.run')}</span>
            </Button>
          </div>
        </div>
      ) : null}
      {showChrome && !modelOptions.length ? (
        <p className="cn-gen-controls__warn">{t('canvas.errors.noModel')}</p>
      ) : null}
    </div>
  )
})
